import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import { IAppState } from '../../../store/Store';
import { TTasksState } from '../../../types/Tasks';
import {
  getTaskDetail,
  addTaskComment,
  deleteTaskComment,
  uploadTaskFile,
  deleteTaskFile,
  updateTask,
  submitConclusion,
} from '../../../actions/TaskActions';
import { setCurrentPageTitle } from '../../../actions/ContentActions';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import { Alert } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import RepeatIcon from '@mui/icons-material/Repeat';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { simpleDateTimeFormat } from '../../../Helpers';
import { TaskPriorityType } from '../../../types/Tasks';
import { TDispatch } from '../../../types/Thunk';
import TaskCommentsSection from '../../../components/tasks/TaskCommentsSection';
import TaskFilesSection from '../../../components/tasks/TaskFilesSection';
import TaskUpdateSection from '../../../components/tasks/TaskUpdateSection';
import TaskDetailsTable from '../../../components/tasks/TaskDetailsTable';
import ServiceConclusionSection from '../../../components/tasks/ServiceConclusionSection';
import { useUser } from '../../../hooks/useUser';
import LoginPage from '../auth/LoginPage';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginBottom: theme.spacing(2),
    },
    title: {
      fontWeight: 600,
      fontSize: '1.25rem',
      marginBottom: theme.spacing(1),
    },
    metaRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      flexWrap: 'wrap',
    },
    dueDateOverdue: {
      color: theme.palette.error.main,
      fontSize: '0.875rem',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
    },
    dueDateFuture: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
    },
    dueDateIcon: {
      fontSize: '1.1rem',
      marginRight: 4,
    },
    recurringIcon: {
      fontSize: '1.1rem',
      color: theme.palette.text.secondary,
    },
    priorityBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    priorityCircle: {
      width: 18,
      height: 18,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    priorityLabel: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    description: {
      marginBottom: theme.spacing(2),
      fontSize: '0.9rem',
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      '& p': {
        margin: theme.spacing(0, 0, 1),
      },
      '& img': {
        maxWidth: '100%',
      },
    },
    divider: {
      borderStyle: 'dashed',
      borderColor: '#ccc',
      borderWidth: 1,
      borderRadius: 1,
      margin: `${theme.spacing(2)} 0`,
    },
    updateButton: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      width: '100%',
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
  })
);

const isDueDatePast = (dueDate: string | null): boolean => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

const getPriorityIcon = (priorityType: TaskPriorityType | null) => {
  switch (priorityType) {
    case TaskPriorityType.Low:
      return {
        icon: <ArrowDownwardIcon style={{ color: '#fff', fontSize: '1rem' }} />,
        bgColor: '#999',
      };
    case TaskPriorityType.Medium:
      return {
        icon: <ArrowUpwardIcon style={{ color: '#fff', fontSize: '1rem' }} />,
        bgColor: '#2196f3',
      };
    case TaskPriorityType.Important:
      return {
        icon: <ArrowUpwardIcon style={{ color: '#fff', fontSize: '1rem' }} />,
        bgColor: '#ff9800',
      };
    case TaskPriorityType.Urgent:
      return {
        icon: <ArrowUpwardIcon style={{ color: '#fff', fontSize: '1rem' }} />,
        bgColor: '#f44336',
      };
    default:
      return null;
  }
};

const TaskDetailPage: React.FC<
  RouteComponentProps<{ id: string | undefined }>
> = ({
  match: {
    params: { id },
  },
  history,
}): JSX.Element => {
  const dispatch = useDispatch<TDispatch>();
  const { t } = useTranslation();
  const classes = useStyles({});
  const { isImpersonated, isUserAnonymous } = useUser();
  const readOnly = isImpersonated();
  const tasksState: TTasksState = useSelector<IAppState, TTasksState>(
    (state) => state.tasksState
  );

  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [selectedAssigneeEmail, setSelectedAssigneeEmail] = useState<
    string | null
  >(null);
  const [notifyAssignee, setNotifyAssignee] = useState(false);
  const [notifyAssigneeComment, setNotifyAssigneeComment] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    dispatch(setCurrentPageTitle(t('tasks.detail.page_title')));
  }, [dispatch, t]);

  useEffect(() => {
    if (id) {
      dispatch(getTaskDetail(parseInt(id)));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (tasksState.detail) {
      const task = tasksState.detail.task;
      setSelectedStatusId(task.statusId);
      setSelectedAssigneeEmail(task.assigneeEmail);
    }
  }, [tasksState.detail]);

  const handleAddComment = (comment: string) => {
    if (id) {
      dispatch(addTaskComment(parseInt(id), comment));
    }
  };

  const handleDeleteComment = (commentId: number) => {
    if (id) {
      dispatch(deleteTaskComment(parseInt(id), commentId));
    }
  };

  const handleUploadFile = (filename: string, content: string) => {
    if (id) {
      dispatch(uploadTaskFile(parseInt(id), filename, content));
    }
  };

  const handleDeleteFile = (fileId: number) => {
    if (id) {
      dispatch(deleteTaskFile(parseInt(id), fileId));
    }
  };

  const handleAssigneeChange = (
    assigneeEmail: string | null,
    notify: boolean,
    comment: string
  ) => {
    setSelectedAssigneeEmail(assigneeEmail);
    setNotifyAssignee(notify);
    setNotifyAssigneeComment(comment);
  };

  const handleSubmitConclusion = (conclusionStatus: number) => {
    if (id) {
      dispatch(submitConclusion(parseInt(id), conclusionStatus));
    }
  };

  const handleUpdate = () => {
    if (id) {
      const originalTask = tasksState.detail?.task;
      const assigneeChanged =
        originalTask &&
        selectedAssigneeEmail !== originalTask.assigneeEmail;

      dispatch(
        updateTask(parseInt(id), {
          statusId: selectedStatusId,
          assigneeEmail: selectedAssigneeEmail,
          notifyAssignee,
          notifyAssigneeComment: notifyAssignee ? notifyAssigneeComment : null,
        })
      ).then(() => {
        if (assigneeChanged) {
          window.location.href = '/app/tasks';
        } else {
          setShowSuccess(true);
        }
      });
    }
  };

  if (isUserAnonymous()) {
    localStorage.removeItem('token');
    return <LoginPage fallbackUrl={`/tasks/${id}`} />;
  }

  if (tasksState.detailLoading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tasksState.detail) {
    return <></>;
  }

  const { task, comments, files, statuses, assignees, unreadCommentCount } =
    tasksState.detail;

  const overdue = isDueDatePast(task.dueDate);
  const priority = getPriorityIcon(task.priorityType);
  const formattedDueDate = task.dueDate
    ? simpleDateTimeFormat(new Date(task.dueDate))
    : null;

  return (
    <Box>
      <Box className={classes.header}>
        <h2>{task.title}</h2>
        <Box className={classes.metaRow}>
          {task.dueDate && (
            <Typography
              component="span"
              className={overdue ? classes.dueDateOverdue : classes.dueDateFuture}
            >
              <EventIcon
                className={classes.dueDateIcon}
                style={{ color: overdue ? '#f44336' : undefined }}
              />
              {formattedDueDate}
            </Typography>
          )}
          {task.isRecurring && (
            <RepeatIcon
              className={classes.recurringIcon}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                document
                  .getElementById('task-schedule-section')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            />
          )}
          {priority && task.priorityLabel && (
            <Box className={classes.priorityBadge}>
              <div
                className={classes.priorityCircle}
                style={{ backgroundColor: priority.bgColor }}
              >
                {priority.icon}
              </div>
              <Typography component="span" className={classes.priorityLabel}>
                {task.priorityLabel}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {task.serviceTaskType != null && (
        <ServiceConclusionSection
          task={task}
          onSubmitConclusion={handleSubmitConclusion}
          readOnly={readOnly}
        />
      )}

      {task.description && (
        <Box
          className={classes.description}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(task.description) }}
        />
      )}

      <TaskCommentsSection
        comments={comments}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        unreadCommentCount={unreadCommentCount ?? 0}
        readOnly={readOnly}
      />

      <div className={classes.divider} />

      <TaskUpdateSection
        statuses={statuses}
        assignees={assignees}
        selectedStatusId={selectedStatusId}
        selectedAssigneeEmail={selectedAssigneeEmail}
        onStatusChange={setSelectedStatusId}
        onAssigneeChange={handleAssigneeChange}
        readOnly={readOnly}
        statusReadOnly={task.serviceTaskType != null && task.conclusion}
      />

      <TaskFilesSection
        files={files}
        onUploadFile={handleUploadFile}
        onDeleteFile={handleDeleteFile}
        readOnly={readOnly}
      />

      {!readOnly && (
        <Button
          variant="contained"
          color="primary"
          className={classes.updateButton}
          onClick={handleUpdate}
        >
          {t('tasks.detail.update_task')}
        </Button>
      )}

      <TaskDetailsTable task={task} />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          {t('tasks.detail.update_success')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default withRouter(TaskDetailPage);
